import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { metricsAPI } from "../services/api";
import { usePageTitle } from "../contexts/PageTitleContext";
import PageHeader from "../components/layout/PageHeader";
import { Eye } from "lucide-react";
import { useCan } from "../hooks/useCan";
import { useAgent } from "../contexts/AgentContext";
import MetricForm from "../components/metrics/MetricForm";

const CustomMetrics = () => {
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();
  const navigate = useWsNavigate();
  const location = useLocation();
  const { isAuthenticated, currentWorkspace } = useAuth();
  const { showToast } = useToast();
  const { agents, selectedAgent } = useAgent();
  const canWrite = useCan("custom_metric", "write");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editMetricId, setEditMetricId] = useState(null);
  const [initialMetric, setInitialMetric] = useState(null);

  useEffect(() => {
    setPageTitle("Custom Metrics");
    setParentPage({ name: "Metrics", href: "/evaluation/metrics" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [setPageTitle, setParentPage, setShowBackButton]);

  useEffect(() => {
    if (!isAuthenticated) navigate("/login");
  }, [isAuthenticated, navigate]);

  const fetchMetricForEdit = async (id) => {
    try {
      const metric = await metricsAPI.getMetrics(currentWorkspace.id, id);
      setInitialMetric(metric);
    } catch (err) {
      console.error("Error fetching metric:", err);
      showToast("Failed to load metric for editing", "error");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    if (editId && currentWorkspace?.id) {
      setIsEditMode(true);
      setEditMetricId(editId);
      fetchMetricForEdit(editId);
    }
  }, [location.search, currentWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (payload, meta) => {
    try {
      let response;
      if (isEditMode && editMetricId && meta.isSystemMetric) {
        const agentId = meta.agentId || selectedAgent?.id;
        if (!agentId) {
          showToast("Select an agent to save this system metric override", "error");
          return;
        }
        response = await metricsAPI.updatePromptOverride(
          currentWorkspace.id,
          editMetricId,
          agentId,
          meta.llmInstructions,
        );
        if (response) {
          showToast("Metric prompt saved for this agent!", "success");
          navigate("/evaluation/metrics");
        }
      } else if (isEditMode && editMetricId) {
        response = await metricsAPI.updateMetric(editMetricId, { metric: payload });
        if (response) {
          showToast("Metric updated successfully!", "success");
          navigate("/evaluation/metrics");
        }
      } else {
        response = await metricsAPI.createMetric({ metric: payload });
        if (response) {
          showToast("Metric created successfully!", "success");
          navigate("/evaluation/metrics");
        }
      }
      if (!response) throw new Error("Failed to save metric");
    } catch (err) {
      console.error("Error saving metric:", err);
      const data = err.response?.data;
      const apiMsg =
        data?.error ||
        data?.errors?.base?.[0] ||
        (Array.isArray(data?.errors) ? data.errors[0] : null) ||
        Object.values(data?.errors || {})[0]?.[0];
      showToast(
        apiMsg || (isEditMode ? "Failed to update metric" : "Failed to create metric"),
        "error",
      );
    }
  };

  const handleResetSystemPrompt = async () => {
    const agentId = initialMetric?.agent_ids?.[0] || selectedAgent?.id;
    if (!agentId) {
      showToast("Select an agent first", "error");
      return;
    }
    if (!window.confirm("Reset this metric's prompt for this agent back to the default?")) return;
    try {
      await metricsAPI.deletePromptOverride(currentWorkspace.id, editMetricId, agentId);
      showToast("Reset to default prompt", "success");
      fetchMetricForEdit(editMetricId);
    } catch (err) {
      console.error(err);
      showToast("Failed to reset override", "error");
    }
  };

  return (
    <div className="px-4">
      <PageHeader description="Create and manage custom metrics for your evaluations." />

      {!canWrite && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 text-sm rounded-lg flex items-center gap-2">
          <Eye className="h-4 w-4 flex-shrink-0" /> View only — your role doesn't
          have write access
        </div>
      )}

      <MetricForm
        mode={isEditMode ? "edit" : "create"}
        initialMetric={initialMetric}
        showAssignment
        canWrite={canWrite}
        agents={agents}
        clientId={currentWorkspace?.id}
        defaultAgentId={selectedAgent?.id || ""}
        onSubmit={handleSubmit}
        onCancel={() => navigate("/evaluation/metrics")}
        onResetSystemPrompt={
          isEditMode ? handleResetSystemPrompt : undefined
        }
        notify={showToast}
      />
    </div>
  );
};

export default CustomMetrics;
